//@ts-nocheck
import Tabs from "@/components/Tabs/Tabs";
import { useState, useEffect } from "react";
import Image from "next/image";
import { Button } from "@/components/Button/Button";
import { useRouter } from "next/router";
import Link from "next/link";
const Extended = ({ data, content }) => {
  const { header = "Header", subheader = "Subheader" } = content;
  const router = useRouter();
  const { step, tab, column } = router.query;

  // Initialize state with URL parameters or defaults
  const [vehicleType, setVehicleType] = useState(step || Object.keys(data)[0]);
  const [configuration, setConfiguration] = useState(
    tab || Object.keys(data[vehicleType])[0]
  );
  const [weightClass, setWeightClass] = useState(column || null);
  const [highlightedColumn, setHighlightedColumn] = useState(null);
  const [hoveredColumn, setHoveredColumn] = useState(null);

  // Update state when URL parameters change
  useEffect(() => {
    // Only run on client side and when router is ready
    if (!router.isReady) return;

    if (step) setVehicleType(step);
    if (tab) setConfiguration(tab);
    if (column) {
      setWeightClass(column);
      // Find the column index to highlight
      const weightClasses = data[vehicleType]?.[configuration];
      if (weightClasses) {
        const availableWeightClasses = Object.keys(weightClasses).filter(
          (key) => weightClasses[key]?.data
        );
        const columnIndex = availableWeightClasses.indexOf(column);
        if (columnIndex !== -1) {
          setHighlightedColumn(columnIndex);
        }
      }
    }
  }, [router.isReady, step, tab, column, data, vehicleType, configuration]);

  const handleTabChange = (newVehicleType) => {
    // Get the first available configuration for the new vehicle type
    const firstConfig = Object.keys(data[newVehicleType])[0];

    setVehicleType(newVehicleType);
    setConfiguration(firstConfig);
    setWeightClass(null);
    setHighlightedColumn(null);
    // Update URL without page reload
    router.push(
      {
        pathname: router.asPath.split("?")[0], // Use current path without query params
        query: {
          tab: firstConfig,
          step: newVehicleType,
          ...(weightClass && { column: weightClass }),
        },
      },
      undefined,
      { shallow: true }
    );
  };

  const handleConfigChange = (newConfig) => {
    setConfiguration(newConfig);
    setWeightClass(null);
    setHighlightedColumn(null);
    // Update URL without page reload
    router.push(
      {
        pathname: router.asPath.split("?")[0],
        query: {
          tab: newConfig,
          step: vehicleType,
          ...(weightClass && { column: weightClass }),
        },
      },
      undefined,
      { shallow: true }
    );
  };

  const handleMouseEnter = (index) => {
    setHoveredColumn(index);
  };

  const handleMouseLeave = () => {
    setHoveredColumn(null);
  };

  const renderTable = (weightClasses) => {
    if (!weightClasses) return null;

    // Get all weight classes that have data
    const availableWeightClasses = Object.keys(weightClasses).filter(
      (key) => weightClasses[key]?.data
    );

    if (availableWeightClasses.length === 0) return null;

    // Get the first weight class's link data
    const firstWeightClass = availableWeightClasses[0];
    const linkData = weightClasses[firstWeightClass]?.link;

    // Get all unique headers from all weight classes
    const headers = new Set();
    availableWeightClasses.forEach((weightClass) => {
      if (weightClasses[weightClass]?.data) {
        Object.keys(weightClasses[weightClass].data).forEach((key) =>
          headers.add(key)
        );
      }
    });

    const headerArray = Array.from(headers);

    return (
      <div className="flex flex-col gap-8">
        <div className="overflow-x-auto -mx-4 md:mx-0">
          <table className="table-fixed min-w-full border-separate border-spacing-0">
            <thead>
              <tr>
                <th className="bg-blue-dark text-white p-6 text-left font-medium border-r border-white">
                  Spezifikation
                </th>
                {availableWeightClasses.map((weightClass, colIndex) => (
                  <th
                    key={weightClass}
                    onMouseEnter={() => handleMouseEnter(colIndex)}
                    onMouseLeave={handleMouseLeave}
                    className={`bg-blue-dark text-white p-6 text-center font-medium border-r border-white last:border-r-0 ${
                      highlightedColumn === colIndex
                        ? "bg-blue-light"
                        : hoveredColumn === colIndex
                        ? "bg-blue-light/50"
                        : ""
                    }`}
                  >
                    Nutzlastklasse {weightClass}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {headerArray.map((header, rowIndex) => {
                const isNaNRow = availableWeightClasses.some(
                  (weightClass) =>
                    weightClasses[weightClass]?.data[header] === "type_header"
                );

                if (isNaNRow) {
                  return (
                    <tr key={rowIndex} className="bg-blue-medium text-white">
                      <td
                        colSpan={availableWeightClasses.length + 1}
                        className="text-left font-bold p-6 whitespace-normal"
                      >
                        {header.replace(/_/g, " ")}
                      </td>
                    </tr>
                  );
                }

                return (
                  <tr
                    key={header}
                    className={rowIndex % 2 === 0 ? "bg-gray-50" : "bg-white"}
                  >
                    <td className="border-y-2 border-white font-bold bg-gray-100 p-6 !max-w-[250px]">
                      {header.replace(/_/g, " ")}
                    </td>
                    {availableWeightClasses.map((weightClass, colIndex) => (
                      <td
                        key={`${weightClass}-${header}`}
                        onMouseEnter={() => handleMouseEnter(colIndex)}
                        onMouseLeave={handleMouseLeave}
                        className={`border-2 border-gray-100 px-10 text-center text-gray-600 py-2 whitespace ${
                          highlightedColumn === colIndex
                            ? "bg-[#008CBA29]"
                            : hoveredColumn === colIndex
                            ? "bg-[#008CBA29]/50"
                            : ""
                        }`}
                      >
                        {weightClasses[weightClass]?.data[header] || "—"}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {linkData && (
          <div className="flex w-full justify-center">
            <Link
              href={linkData.href}
              target={linkData.target}
              className="w-full"
            >
              <Button type="primary" className="w-full !min-w-screen">
                {linkData.linkText}
              </Button>
            </Link>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="container mx-auto">
      <div className="flex flex-col items-center justify-center py-12">
        <h1 className="lg:text-7xl text-5xl font-bold text-blue-dark mb-2">
          {header}
        </h1>
        <h2 className="lg:text-5xl text-3xl font-normal text-blue-dark text-center max-w-2xl">
          {subheader}
        </h2>
      </div>

      <div className="mb-8">
        <Tabs.Root
          value={vehicleType}
          onValueChange={handleTabChange}
          className="relative"
        >
          <Tabs.List className="w-full lg:overflow-y-visible overflow-y-scroll flex mb-8">
            <div className="flex lg:justify-center justify-start pb-8">
              {Object.keys(data).map((type) => (
                <Tabs.Trigger
                  value={type}
                  key={type}
                  className="active:text-blue-light hover:text-blue-medium hover:border-b-4 active:border-b-4 active:font-bold text-gray-600 px-8 pt-4 pb-8 border-b-2 border-gray-300 active:border-orange-light hover:border-orange"
                >
                  <span className="whitespace-nowrap">{type}</span>
                </Tabs.Trigger>
              ))}
            </div>
          </Tabs.List>

          {Object.entries(data).map(([type, configs]) => (
            <Tabs.Content key={type} value={type}>
              <Tabs.Root
                value={configuration}
                onValueChange={handleConfigChange}
                className="relative"
              >
                <Tabs.List className="w-full flex mb-8">
                  <div className="flex w-full lg:justify-center items-stretch justify-start gap-4">
                    {Object.keys(configs).map((config) => (
                      <Tabs.Trigger
                        value={config}
                        key={config}
                        className=" p-5 bg-white  w-full rounded-6 text-blue-light border-2 border-blue-light active:text-white font-bold active:bg-blue-light"
                      >
                        <span className="whitespace-nowrap">{config}</span>
                      </Tabs.Trigger>
                    ))}
                  </div>
                </Tabs.List>

                {Object.entries(configs).map(([config, weightClasses]) => (
                  <Tabs.Content key={config} value={config}>
                    {renderTable(weightClasses)}
                  </Tabs.Content>
                ))}
              </Tabs.Root>
            </Tabs.Content>
          ))}
        </Tabs.Root>
      </div>
    </div>
  );
};

export default Extended;

const jsn = {
  content: {
    colPos1: [
      {
        id: 8350,
        type: "bracar_carmassandweight",
        colPos: 1,
        categories: "",
        appearance: {
          layout: "default",
          frameClass: "0",
          spaceBefore: "",
          spaceAfter: "",
        },
        content: {
          header: "Maße und Gewichte",
          subheader: "Ford Transit",
          headerLayout: 0,
          headerPosition: "",
          headerLink: "",
          data: {
            modelConfigs: [
              13, 15, 23, 14, 24, 25, 26, 27, 28, 29, 30, 18, 19, 22, 21, 20,
            ],
            tableType: 1,
            table: {
              "Transit Kasten": {
                L2H2: {
                  "290": {
                    data: {
                      "Maße Außen": "type_header",
                      "Länge (mm)": "5.531 mm",
                      "Breite (mm)": "2.474 mm",
                      "Radstand (mm)": "3.300 mm",
                      "Überhang hinten mit Anhängerkupplung (mm)": "1.367 mm",
                      "Öffnungsbreite, Schiebetür (mm)": "1.300 mm",
                      "Öffnungshöhe, Heckladetür (mm)": "1.648 - 1.748 mm",
                      "Laderaumhöhe (mm)": "1.786 - 1.886 mm",
                      "Fahrzeugbreite ohne Außenspiegel (mm)": "2.059 mm",
                      "Maße innen": "type_header",
                      "Laderaumbreite zwischen den Radkästen am Boden (mm)":
                        "1.472 mm",
                      "Maximale Laderaumbreite (mm)": "1.784 mm",
                      "Ladevolumen (m³)": "9,5 - 11,5 m³",
                      Gewichte: "type_header",
                      "Zulässiges Gesamtgewicht (kg)": "2.940 kg",
                      "Nutzlast (kg)": "772 - 899 kg",
                      "Leerachslast, vorn (kg)": "1.271 - 1.350 kg",
                      "Leerachslast, hinten (kg)": "769 - 817 kg",
                      "Anhängelast gebremst (kg)": "2.450 - 2.550 kg",
                      "Anhängelast ungebremst (kg)": "750 kg",
                      "Zulässiges Gesamtgewicht des Zuges (kg)":
                        "4.940 - 5.140 kg",
                      Antrieb: "Frontantrieb",
                      Motor:
                        "2.0 l EcoBlue 77 kW / 105 PS, 2.0 l EcoBlue 96 kW / 130 PS",
                      Getriebe: "6-Gang-Schaltgetriebe (VMT6)",
                    },
                    image:
                      "https://dev.api.ford-koegler.de/fileadmin/mass_and_weights/Transit/L2H2.jpg",
                    link: {
                      href: "/transit-kastenwagen/",
                      target: null,
                      class: null,
                      title: null,
                      linkText: "Ford Transit Kastenwagen",
                      additionalAttributes: [],
                    },
                  },
                  "310": {
                    data: {
                      "Maße Außen": "type_header",
                      "Länge (mm)": "5.531 mm",
                      "Breite (mm)": "2.474 mm",
                      "Radstand (mm)": "3.300 mm",
                      "Überhang hinten mit Anhängerkupplung (mm)": "1.367 mm",
                      "Öffnungsbreite, Schiebetür (mm)": "1.300 mm",
                      "Öffnungshöhe, Heckladetür (mm)": "1.648 - 1.748 mm",
                      "Laderaumhöhe (mm)": "1.786 - 1.886 mm",
                      "Fahrzeugbreite ohne Außenspiegel (mm)": "2.059 mm",
                      "Maße innen": "type_header",
                      "Laderaumbreite zwischen den Radkästen am Boden (mm)":
                        "1.472 mm",
                      "Maximale Laderaumbreite (mm)": "1.784 mm",
                      "Ladevolumen (m³)": "9,5 - 11,5 m³",
                      Gewichte: "type_header",
                      "Zulässiges Gesamtgewicht (kg)": "3.140 kg",
                      "Nutzlast (kg)": "1.040 - 1.113 kg",
                      "Leerachslast, vorn (kg)": "1.309 - 1.354 kg",
                      "Leerachslast, hinten (kg)": "776 - 786 kg",
                      "Anhängelast gebremst (kg)": "2.750 - 2.800 kg",
                      "Anhängelast ungebremst (kg)": "750 kg",
                      "Zulässiges Gesamtgewicht des Zuges (kg)":
                        "5.500 - 5.940 kg",
                      Antrieb: "Frontantrieb",
                      Motor:
                        "2.0 l EcoBlue 121 kW / 165 PS, 2.0 l EcoBlue 77 kW / 105 PS, 2.0 l EcoBlue 96 kW / 130 PS",
                      Getriebe:
                        "6-Gang-Schaltgetriebe (VMT6), 8-Gang-Automatikgetriebe (8F57)",
                    },
                    image:
                      "https://dev.api.ford-koegler.de/fileadmin/mass_and_weights/Transit/L2H2.jpg",
                    link: {
                      href: "/transit-kastenwagen/",
                      target: null,
                      class: null,
                      title: null,
                      linkText: "Ford Transit Kastenwagen",
                      additionalAttributes: [],
                    },
                  },
                  "350": {
                    data: {
                      "Maße Außen": "type_header",
                      "Länge (mm)": "5.531 mm",
                      "Breite (mm)": "2.474 mm",
                      "Radstand (mm)": "3.300 mm",
                      "Überhang hinten mit Anhängerkupplung (mm)": "1.367 mm",
                      "Öffnungsbreite, Schiebetür (mm)": "1.300 mm",
                      "Öffnungshöhe, Heckladetür (mm)": "1.648 - 1.748 mm",
                      "Laderaumhöhe (mm)": "1.786 - 1.886 mm",
                      "Fahrzeugbreite ohne Außenspiegel (mm)": "2.059 mm",
                      "Maße innen": "type_header",
                      "Laderaumbreite zwischen den Radkästen am Boden (mm)":
                        "1.472 mm",
                      "Maximale Laderaumbreite (mm)": "1.784 mm",
                      "Ladevolumen (m³)": "9,5 - 11,5 m³",
                      Gewichte: "type_header",
                      "Zulässiges Gesamtgewicht (kg)": "3.500 kg",
                      "Nutzlast (kg)": "1.228 - 1.449 kg",
                      "Leerachslast, vorn (kg)": "1.312 - 1.366 kg",
                      "Leerachslast, hinten (kg)": "796 - 945 kg",
                      "Anhängelast gebremst (kg)": "2.750 - 2.800 kg",
                      "Anhängelast ungebremst (kg)": "750 kg",
                      "Zulässiges Gesamtgewicht des Zuges (kg)":
                        "5.500 - 6.000 kg",
                      Antrieb: "Allradantrieb, Frontantrieb",
                      Motor:
                        "2.0 l EcoBlue 121 kW / 165 PS, 2.0 l EcoBlue 77 kW / 105 PS, 2.0 l EcoBlue 96 kW / 130 PS",
                      Getriebe:
                        "6-Gang-Schaltgetriebe (VMT6), 6-Gang-Schaltgetriebe MT82 Allradantrieb, 8-Gang-Automatikgetriebe (8F57)",
                    },
                    image:
                      "https://dev.api.ford-koegler.de/fileadmin/mass_and_weights/Transit/L2H2.jpg",
                    link: {
                      href: "/transit-kastenwagen/",
                      target: null,
                      class: null,
                      title: null,
                      linkText: "Ford Transit Kastenwagen",
                      additionalAttributes: [],
                    },
                  },
                },
                L2H3: {
                  "310": {
                    data: {
                      "Maße Außen": "type_header",
                      "Länge (mm)": "5.531 mm",
                      "Breite (mm)": "2.474 mm",
                      "Radstand (mm)": "3.300 mm",
                      "Überhang hinten mit Anhängerkupplung (mm)": "1.367 mm",
                      "Öffnungsbreite, Schiebetür (mm)": "1.300 mm",
                      "Öffnungshöhe, Heckladetür (mm)": "1.887 - 1.987 mm",
                      "Laderaumhöhe (mm)": "2.025 - 2.125 mm",
                      "Fahrzeugbreite ohne Außenspiegel (mm)": "2.059 mm",
                      "Maße innen": "type_header",
                      "Laderaumbreite zwischen den Radkästen am Boden (mm)":
                        "1.472 mm",
                      "Maximale Laderaumbreite (mm)": "1.784 mm",
                      "Ladevolumen (m³)": "10,7 - 13,0 m³",
                      Gewichte: "type_header",
                      "Zulässiges Gesamtgewicht (kg)": "3.140 kg",
                      "Nutzlast (kg)": "998 - 1.071 kg",
                      "Leerachslast, vorn (kg)": "1.318 - 1.363 kg",
                      "Leerachslast, hinten (kg)": "809 - 819 kg",
                      "Anhängelast gebremst (kg)": "2.750 - 2.800 kg",
                      "Anhängelast ungebremst (kg)": "750 kg",
                      "Zulässiges Gesamtgewicht des Zuges (kg)":
                        "5.500 - 5.940 kg",
                      Antrieb: "Frontantrieb",
                      Motor:
                        "2.0 l EcoBlue 121 kW / 165 PS, 2.0 l EcoBlue 77 kW / 105 PS, 2.0 l EcoBlue 96 kW / 130 PS",
                      Getriebe:
                        "6-Gang-Schaltgetriebe (VMT6), 8-Gang-Automatikgetriebe (8F57)",
                    },
                    image:
                      "https://dev.api.ford-koegler.de/fileadmin/mass_and_weights/Transit/L2H2.jpg",
                    link: {
                      href: "/transit-kastenwagen/",
                      target: null,
                      class: null,
                      title: null,
                      linkText: "Ford Transit Kastenwagen",
                      additionalAttributes: [],
                    },
                  },
                  "350": {
                    data: {
                      "Maße Außen": "type_header",
                      "Länge (mm)": "5.531 mm",
                      "Breite (mm)": "2.474 mm",
                      "Radstand (mm)": "3.300 mm",
                      "Überhang hinten mit Anhängerkupplung (mm)": "1.367 mm",
                      "Öffnungsbreite, Schiebetür (mm)": "1.300 mm",
                      "Öffnungshöhe, Heckladetür (mm)": "1.887 - 1.987 mm",
                      "Laderaumhöhe (mm)": "2.025 - 2.125 mm",
                      "Fahrzeugbreite ohne Außenspiegel (mm)": "2.059 mm",
                      "Maße innen": "type_header",
                      "Laderaumbreite zwischen den Radkästen am Boden (mm)":
                        "1.472 mm",
                      "Maximale Laderaumbreite (mm)": "1.784 mm",
                      "Ladevolumen (m³)": "10,7 - 13,0 m³",
                      Gewichte: "type_header",
                      "Zulässiges Gesamtgewicht (kg)": "3.500 kg",
                      "Nutzlast (kg)": "1.186 - 1.408 kg",
                      "Leerachslast, vorn (kg)": "1.321 - 1.375 kg",
                      "Leerachslast, hinten (kg)": "829 - 978 kg",
                      "Anhängelast gebremst (kg)": "2.750 - 2.800 kg",
                      "Anhängelast ungebremst (kg)": "750 kg",
                      "Zulässiges Gesamtgewicht des Zuges (kg)":
                        "5.500 - 6.000 kg",
                      Antrieb: "Allradantrieb, Frontantrieb",
                      Motor:
                        "2.0 l EcoBlue 121 kW / 165 PS, 2.0 l EcoBlue 77 kW / 105 PS, 2.0 l EcoBlue 96 kW / 130 PS",
                      Getriebe:
                        "6-Gang-Schaltgetriebe (VMT6), 6-Gang-Schaltgetriebe MT82 Allradantrieb, 8-Gang-Automatikgetriebe (8F57)",
                    },
                    image:
                      "https://dev.api.ford-koegler.de/fileadmin/mass_and_weights/Transit/L2H2.jpg",
                    link: {
                      href: "/transit-kastenwagen/",
                      target: null,
                      class: null,
                      title: null,
                      linkText: "Ford Transit Kastenwagen",
                      additionalAttributes: [],
                    },
                  },
                },
                L3H2: {
                  "310": {
                    data: {
                      "Maße Außen": "type_header",
                      "Länge (mm)": "5.981 mm",
                      "Breite (mm)": "2.474 mm",
                      "Radstand (mm)": "3.750 mm",
                      "Überhang hinten mit Anhängerkupplung (mm)": "1.367 mm",
                      "Öffnungsbreite, Schiebetür (mm)": "1.300 mm",
                      "Öffnungshöhe, Heckladetür (mm)": "1.648 - 1.748 mm",
                      "Laderaumhöhe (mm)": "1.786 - 1.886 mm",
                      "Fahrzeugbreite ohne Außenspiegel (mm)": "2.059 mm",
                      "Maße innen": "type_header",
                      "Laderaumbreite zwischen den Radkästen am Boden (mm)":
                        "1.472 mm",
                      "Maximale Laderaumbreite (mm)": "1.784 mm",
                      "Ladevolumen (m³)": "11,0 - 11,5 m³",
                      Gewichte: "type_header",
                      "Zulässiges Gesamtgewicht (kg)": "3.140 kg",
                      "Nutzlast (kg)": "1.040 - 1.058 kg",
                      "Leerachslast, vorn (kg)": "1.345 kg",
                      "Leerachslast, hinten (kg)": "795 kg",
                      "Anhängelast gebremst (kg)": "2.750 - 2.800 kg",
                      "Anhängelast ungebremst (kg)": "750 kg",
                      "Zulässiges Gesamtgewicht des Zuges (kg)":
                        "5.500 - 5.700 kg",
                      Antrieb: "Frontantrieb",
                      Motor:
                        "2.0 l EcoBlue 121 kW / 165 PS, 2.0 l EcoBlue 77 kW / 105 PS, 2.0 l EcoBlue 96 kW / 130 PS",
                      Getriebe: "6-Gang-Schaltgetriebe (VMT6)",
                    },
                    image:
                      "https://dev.api.ford-koegler.de/fileadmin/mass_and_weights/Transit/L2H2.jpg",
                    link: {
                      href: "/transit-kastenwagen/",
                      target: null,
                      class: null,
                      title: null,
                      linkText: "Ford Transit Kastenwagen",
                      additionalAttributes: [],
                    },
                  },
                  "350": {
                    data: {
                      "Maße Außen": "type_header",
                      "Länge (mm)": "5.981 mm",
                      "Breite (mm)": "2.474 mm",
                      "Radstand (mm)": "3.750 mm",
                      "Überhang hinten mit Anhängerkupplung (mm)": "1.367 mm",
                      "Öffnungsbreite, Schiebetür (mm)": "1.300 mm",
                      "Öffnungshöhe, Heckladetür (mm)": "1.648 - 1.748 mm",
                      "Laderaumhöhe (mm)": "1.786 - 1.886 mm",
                      "Fahrzeugbreite ohne Außenspiegel (mm)": "2.059 mm",
                      "Maße innen": "type_header",
                      "Laderaumbreite zwischen den Radkästen am Boden (mm)":
                        "1.472 mm",
                      "Maximale Laderaumbreite (mm)": "1.784 mm",
                      "Ladevolumen (m³)": "11,0 - 11,5 m³",
                      Gewichte: "type_header",
                      "Zulässiges Gesamtgewicht (kg)": "3.500 kg",
                      "Nutzlast (kg)": "1.165 - 1.396 kg",
                      "Leerachslast, vorn (kg)": "1.333 - 1.412 kg",
                      "Leerachslast, hinten (kg)": "816 - 963 kg",
                      "Anhängelast gebremst (kg)": "2.750 - 3.500 kg",
                      "Anhängelast ungebremst (kg)": "750 kg",
                      "Zulässiges Gesamtgewicht des Zuges (kg)":
                        "5.500 - 7.000 kg",
                      Antrieb: "Allradantrieb, Frontantrieb, Heckantrieb",
                      Motor:
                        "2.0 l EcoBlue 121 kW / 165 PS, 2.0 l EcoBlue 77 kW / 105 PS, 2.0 l EcoBlue 96 kW / 130 PS",
                      Getriebe:
                        "10-Gang-Automatikgetriebe (10R80), 6-Gang-Schaltgetriebe (MT82), 6-Gang-Schaltgetriebe (VMT6), 6-Gang-Schaltgetriebe MT82 Allradantrieb, 8-Gang-Automatikgetriebe (8F57)",
                    },
                    image: null,
                    link: {
                      href: "/pkw-modelle/",
                      target: null,
                      class: null,
                      title: null,
                      linkText: "Ford PKW Modelle",
                      additionalAttributes: [],
                    },
                  },
                },
                L3H3: {
                  "310": {
                    data: {
                      "Maße Außen": "type_header",
                      "Länge (mm)": "5.981 mm",
                      "Breite (mm)": "2.474 mm",
                      "Radstand (mm)": "3.750 mm",
                      "Überhang hinten mit Anhängerkupplung (mm)": "1.367 mm",
                      "Öffnungsbreite, Schiebetür (mm)": "1.300 mm",
                      "Öffnungshöhe, Heckladetür (mm)": "1.887 - 1.987 mm",
                      "Laderaumhöhe (mm)": "2.025 - 2.125 mm",
                      "Fahrzeugbreite ohne Außenspiegel (mm)": "2.059 mm",
                      "Maße innen": "type_header",
                      "Laderaumbreite zwischen den Radkästen am Boden (mm)":
                        "1.472 mm",
                      "Maximale Laderaumbreite (mm)": "1.784 mm",
                      "Ladevolumen (m³)": "12,4 - 13,0 m³",
                      Gewichte: "type_header",
                      "Zulässiges Gesamtgewicht (kg)": "3.140 kg",
                      "Nutzlast (kg)": "913 - 931 kg",
                      "Leerachslast, vorn (kg)": "1.372 kg",
                      "Leerachslast, hinten (kg)": "896 kg",
                      "Anhängelast gebremst (kg)": "2.750 - 2.800 kg",
                      "Anhängelast ungebremst (kg)": "750 kg",
                      "Zulässiges Gesamtgewicht des Zuges (kg)":
                        "5.500 - 5.700 kg",
                      Antrieb: "Frontantrieb",
                      Motor:
                        "2.0 l EcoBlue 121 kW / 165 PS, 2.0 l EcoBlue 77 kW / 105 PS, 2.0 l EcoBlue 96 kW / 130 PS",
                      Getriebe: "6-Gang-Schaltgetriebe (VMT6)",
                    },
                    image: null,
                    link: {
                      href: "/transit-modelle/",
                      target: null,
                      class: null,
                      title: null,
                      linkText: "Transit Modelle",
                      additionalAttributes: [],
                    },
                  },
                  "350": {
                    data: {
                      "Maße Außen": "type_header",
                      "Länge (mm)": "5.981 mm",
                      "Breite (mm)": "2.474 mm",
                      "Radstand (mm)": "3.750 mm",
                      "Überhang hinten mit Anhängerkupplung (mm)": "1.367 mm",
                      "Öffnungsbreite, Schiebetür (mm)": "1.300 mm",
                      "Öffnungshöhe, Heckladetür (mm)": "1.887 - 1.987 mm",
                      "Laderaumhöhe (mm)": "2.025 - 2.125 mm",
                      "Fahrzeugbreite ohne Außenspiegel (mm)": "2.059 mm",
                      "Maße innen": "type_header",
                      "Laderaumbreite zwischen den Radkästen am Boden (mm)":
                        "1.472 mm",
                      "Maximale Laderaumbreite (mm)": "1.784 mm",
                      "Ladevolumen (m³)": "12,4 - 13,0 m³",
                      Gewichte: "type_header",
                      "Zulässiges Gesamtgewicht (kg)": "3.500 kg",
                      "Nutzlast (kg)": "1.120 - 2.118 kg",
                      "Leerachslast, vorn (kg)": "1.345 - 1.423 kg",
                      "Leerachslast, hinten (kg)": "849 - 997 kg",
                      "Anhängelast gebremst (kg)": "2.750 - 3.500 kg",
                      "Anhängelast ungebremst (kg)": "750 kg",
                      "Zulässiges Gesamtgewicht des Zuges (kg)":
                        "5.500 - 7.000 kg",
                      Antrieb: "Allradantrieb, Frontantrieb, Heckantrieb",
                      Motor:
                        "2.0 l EcoBlue 121 kW / 165 PS, 2.0 l EcoBlue 77 kW / 105 PS, 2.0 l EcoBlue 96 kW / 130 PS",
                      Getriebe:
                        "10-Gang-Automatikgetriebe (10R80), 6-Gang-Schaltgetriebe (MT82), 6-Gang-Schaltgetriebe (VMT6), 6-Gang-Schaltgetriebe MT82 Allradantrieb, 8-Gang-Automatikgetriebe (8F57)",
                    },
                    image: null,
                    link: {
                      href: "/transit-modelle/",
                      target: null,
                      class: null,
                      title: null,
                      linkText: "Transit Modelle",
                      additionalAttributes: [],
                    },
                  },
                },
                L4H3: {
                  "350": {
                    data: {
                      "Maße Außen": "type_header",
                      "Länge (mm)": "6.704 mm",
                      "Breite (mm)": "2.474 mm",
                      "Radstand (mm)": "3.750 mm",
                      "Überhang hinten mit Anhängerkupplung (mm)": "2.090 mm",
                      "Öffnungsbreite, Schiebetür (mm)": "1.300 mm",
                      "Öffnungshöhe, Heckladetür (mm)": "1.887 mm",
                      "Laderaumhöhe (mm)": "2.025 mm",
                      "Fahrzeugbreite ohne Außenspiegel (mm)": [
                        "2.059 mm",
                        "2.126 mm",
                        "2.174 mm",
                      ],
                      "Maße innen": "type_header",
                      "Laderaumbreite zwischen den Radkästen am Boden (mm)":
                        "1.472 mm",
                      "Maximale Laderaumbreite (mm)": "1.784 mm",
                      "Ladevolumen (m³)": "15,1 m³",
                      Gewichte: "type_header",
                      "Zulässiges Gesamtgewicht (kg)": "3.500 kg",
                      "Nutzlast (kg)": "1.020 - 1.144 kg",
                      "Leerachslast, vorn (kg)": "1.276 - 1.348 kg",
                      "Leerachslast, hinten (kg)": "1.172 - 1.306 kg",
                      "Anhängelast gebremst (kg)": "2.800 - 3.500 kg",
                      "Anhängelast ungebremst (kg)": "750 kg",
                      "Zulässiges Gesamtgewicht des Zuges (kg)":
                        "5.600 - 7.000 kg",
                      Antrieb: "Allradantrieb, Heckantrieb",
                      Motor:
                        "2.0 l EcoBlue 121 kW / 165 PS, 2.0 l EcoBlue 77 kW / 105 PS, 2.0 l EcoBlue 96 kW / 130 PS",
                      Getriebe:
                        "10-Gang-Automatikgetriebe (10R80), 6-Gang-Schaltgetriebe (MT82), 6-Gang-Schaltgetriebe MT82 Allradantrieb",
                    },
                    image: null,
                    link: {
                      href: "/transit-modelle/",
                      target: null,
                      class: null,
                      title: null,
                      linkText: "Transit Modelle",
                      additionalAttributes: [],
                    },
                  },
                  "500": {
                    data: {
                      "Maße Außen": "type_header",
                      "Länge (mm)": "6.704 mm",
                      "Breite (mm)": "2.474 mm",
                      "Radstand (mm)": "3.750 mm",
                      "Überhang hinten mit Anhängerkupplung (mm)": "2.090 mm",
                      "Öffnungsbreite, Schiebetür (mm)": "1.300 mm",
                      "Öffnungshöhe, Heckladetür (mm)": "1.887 mm",
                      "Laderaumhöhe (mm)": "2.025 mm",
                      "Fahrzeugbreite ohne Außenspiegel (mm)": [
                        "2.059 mm",
                        "2.126 mm",
                        "2.174 mm",
                      ],
                      "Maße innen": "type_header",
                      "Laderaumbreite zwischen den Radkästen am Boden (mm)":
                        "1.472 mm",
                      "Maximale Laderaumbreite (mm)": "1.784 mm",
                      "Ladevolumen (m³)": "15,1 m³",
                      Gewichte: "type_header",
                      "Zulässiges Gesamtgewicht (kg)": "5.000 kg",
                      "Nutzlast (kg)": "2.363 - 2.418 kg",
                      "Leerachslast, vorn (kg)": "1.298 - 1.353 kg",
                      "Leerachslast, hinten (kg)": "1.339 kg",
                      "Anhängelast gebremst (kg)": "3.500 kg",
                      "Anhängelast ungebremst (kg)": "750 kg",
                      "Zulässiges Gesamtgewicht des Zuges (kg)": "7.000 kg",
                      Antrieb: "Heckantrieb",
                      Motor: "2.0 l EcoBlue 121 kW / 165 PS",
                      Getriebe:
                        "10-Gang-Automatikgetriebe (10R80), 6-Gang-Schaltgetriebe (MT82)",
                    },
                    image: null,
                    link: {
                      href: "/transit-modelle/",
                      target: null,
                      class: null,
                      title: null,
                      linkText: "Transit Modelle",
                      additionalAttributes: [],
                    },
                  },
                },
              },
              "Transit Kasten Doka": {
                L2H2: {
                  "350": {
                    data: {
                      "Maße Außen": "type_header",
                      "Länge (mm)": "5.531 mm",
                      "Breite (mm)": "2.474 mm",
                      "Radstand (mm)": "3.300 mm",
                      "Überhang hinten mit Anhängerkupplung (mm)": "1.367 mm",
                      "Öffnungsbreite, Schiebetür (mm)": "1.200 mm",
                      "Öffnungshöhe, Heckladetür (mm)": "1.597 - 1.697 mm",
                      "Laderaumhöhe (mm)": "1.718 - 1.818 mm",
                      "Fahrzeugbreite ohne Außenspiegel (mm)": "2.059 mm",
                      "Maße innen": "type_header",
                      "Laderaumbreite zwischen den Radkästen am Boden (mm)":
                        "1.392 mm",
                      "Maximale Laderaumbreite (mm)": "1.784 mm",
                      "Ladevolumen (m³)": "5,7 - 6,1 m³",
                      Gewichte: "type_header",
                      "Zulässiges Gesamtgewicht (kg)": "3.500 kg",
                      "Nutzlast (kg)": "1.173 - 1.241 kg",
                      "Leerachslast, vorn (kg)": "1.345 - 1.397 kg",
                      "Leerachslast, hinten (kg)": "913 - 931 kg",
                      "Anhängelast gebremst (kg)": "2.150 - 2.800 kg",
                      "Anhängelast ungebremst (kg)": "750 kg",
                      "Zulässiges Gesamtgewicht des Zuges (kg)":
                        "5.065 - 5.700 kg",
                      Antrieb: "Frontantrieb",
                      Motor: "2.0 l EcoBlue 96 kW / 130 PS",
                      Getriebe: "6-Gang-Schaltgetriebe",
                    },
                    image:
                      "https://dev.api.ford-koegler.de/fileadmin/mass_and_weights/Transit/L2H2.jpg",
                    link: {
                      href: "/transit-kastenwagen/",
                      target: null,
                      class: null,
                      title: null,
                      linkText: "Ford Transit Kastenwagen",
                      additionalAttributes: [],
                    },
                  },
                },
                L3H2: {
                  "350": {
                    data: {
                      "Maße Außen": "type_header",
                      "Länge (mm)": "5.981 mm",
                      "Breite (mm)": "2.474 mm",
                      "Radstand (mm)": "3.750 mm",
                      "Überhang hinten mit Anhängerkupplung (mm)": "1.367 mm",
                      "Öffnungsbreite, Schiebetür (mm)": "1.200 mm",
                      "Öffnungshöhe, Heckladetür (mm)": "1.597 - 1.697 mm",
                      "Laderaumhöhe (mm)": "1.718 - 1.818 mm",
                      "Fahrzeugbreite ohne Außenspiegel (mm)": "2.059 mm",
                      "Maße innen": "type_header",
                      "Laderaumbreite zwischen den Radkästen am Boden (mm)":
                        "1.392 mm",
                      "Maximale Laderaumbreite (mm)": "1.784 mm",
                      "Ladevolumen (m³)": "7.2 - 7.6 m³",
                      Gewichte: "type_header",
                      "Zulässiges Gesamtgewicht (kg)": "3.500 kg",
                      "Nutzlast (kg)": "1.108,5 - 1.187,3 kg",
                      "Leerachslast, vorn (kg)": "1.384,3 - 1.444,1 kg",
                      "Leerachslast, hinten (kg)": "928,4 - 947,5 kg",
                      "Anhängelast gebremst (kg)": "2.100 - 2.800 kg",
                      "Anhängelast ungebremst (kg)": "750 kg",
                      "Zulässiges Gesamtgewicht des Zuges (kg)":
                        "5.065 - 6.000 kg",
                      Antrieb: "Frontantrieb",
                      Motor:
                        "2.0 l EcoBlue 121 kW / 165 PS, 2.0 l EcoBlue 96 kW / 130 PS",
                      Getriebe: "6-Gang-Schaltgetriebe",
                    },
                    image: null,
                    link: {
                      href: "/mustang/",
                      target: null,
                      class: null,
                      title: null,
                      linkText: "Ford Mustang",
                      additionalAttributes: [],
                    },
                  },
                },
                L3H3: {
                  "350": {
                    data: {
                      "Maße Außen": "type_header",
                      "Länge (mm)": "5.981 mm",
                      "Breite (mm)": "2.474 mm",
                      "Radstand (mm)": "3.750 mm",
                      "Überhang hinten mit Anhängerkupplung (mm)": "1.367 mm",
                      "Öffnungsbreite, Schiebetür (mm)": "1.200 mm",
                      "Öffnungshöhe, Heckladetür (mm)": "1.836 - 1.936 mm",
                      "Laderaumhöhe (mm)": "2.025 mm",
                      "Fahrzeugbreite ohne Außenspiegel (mm)": "2.059 mm",
                      "Maße innen": "type_header",
                      "Laderaumbreite zwischen den Radkästen am Boden (mm)":
                        "1.154 / 1.392 mm",
                      "Maximale Laderaumbreite (mm)": "1.784 mm",
                      "Ladevolumen (m³)": "8.4 - 12.4 m³",
                      Gewichte: "type_header",
                      "Zulässiges Gesamtgewicht (kg)": "3.500 kg",
                      "Nutzlast (kg)": "1.094,7 - 1.115,4 kg",
                      "Leerachslast, vorn (kg)": "1.419,1 - 1.429,5 kg",
                      "Leerachslast, hinten (kg)": "964,8 - 976,5 kg",
                      "Anhängelast gebremst (kg)": "2.100 - 2.800 kg",
                      "Anhängelast ungebremst (kg)": "750 kg",
                      "Zulässiges Gesamtgewicht des Zuges (kg)":
                        "5.065 - 6.000 kg",
                      Antrieb: "Frontantrieb",
                      Motor: "2.0 l EcoBlue 125 kW / 170 PS",
                      Getriebe: "6-Gang-Schaltgetriebe",
                    },
                    image: null,
                    link: {
                      href: "/mustang/",
                      target: null,
                      class: null,
                      title: null,
                      linkText: "Ford Mustang",
                      additionalAttributes: [],
                    },
                  },
                },
                L4H3: {
                  "350": {
                    data: {
                      "Maße Außen": "type_header",
                      "Länge (mm)": "6.704 mm",
                      "Breite (mm)": "2.474 mm",
                      "Radstand (mm)": "3.750 mm",
                      "Überhang hinten mit Anhängerkupplung (mm)": "2.090 mm",
                      "Öffnungsbreite, Schiebetür (mm)": "1.200 mm",
                      "Öffnungshöhe, Heckladetür (mm)": "1.836 mm",
                      "Laderaumhöhe (mm)": "1.955 mm",
                      "Fahrzeugbreite ohne Außenspiegel (mm)": "2.059 mm",
                      "Maße innen": "type_header",
                      "Laderaumbreite zwischen den Radkästen am Boden (mm)":
                        "1.154 / 1.392 mm",
                      "Maximale Laderaumbreite (mm)": "1.784 mm",
                      "Ladevolumen (m³)": "10.6 m³",
                      Gewichte: "type_header",
                      "Zulässiges Gesamtgewicht (kg)": "3.500 kg",
                      "Nutzlast (kg)": "866.3 - 944.9 kg",
                      "Leerachslast, vorn (kg)": "1.305,0 - 1.357,2 kg",
                      "Leerachslast, hinten (kg)": "1.250,1 - 1.277,2 kg",
                      "Anhängelast gebremst (kg)": "2.550 - 3.500 kg",
                      "Anhängelast ungebremst (kg)": "750 kg",
                      "Zulässiges Gesamtgewicht des Zuges (kg)":
                        "5.065 - 7.000 kg",
                      Antrieb: "Heckantrieb",
                      Motor: "2.0 l EcoBlue 121 kW / 165 PS",
                      Getriebe: "6-Gang-Schaltgetriebe",
                    },
                    image: null,
                    link: {
                      href: "/mustang/",
                      target: null,
                      class: null,
                      title: null,
                      linkText: "Ford Mustang",
                      additionalAttributes: [],
                    },
                  },
                },
              },
              "Transit Kasten Kombi": {
                L2H2: {
                  "350": {
                    data: {
                      "Maße Außen": "type_header",
                      "Länge (mm)": "5.531 mm",
                      "Breite (mm)": "2.474 mm",
                      "Radstand (mm)": "3.300 mm",
                      "Überhang hinten mit Anhängerkupplung (mm)":
                        "1.208 - 1.367 mm",
                      "Öffnungsbreite, Schiebetür (mm)": "1.200 mm",
                      "Öffnungshöhe, Heckladetür (mm)": "1.664 - 1.697 mm",
                      "Laderaumhöhe (mm)": "1.818 mm",
                      "Fahrzeugbreite ohne Außenspiegel (mm)": "2.059 mm",
                      "Maße innen": "type_header",
                      "Laderaumbreite zwischen den Radkästen am Boden (mm)":
                        "1.392 mm",
                      "Maximale Laderaumbreite (mm)": "1.784 mm",
                      "Ladevolumen (m³)": "null",
                      Gewichte: "type_header",
                      "Zulässiges Gesamtgewicht (kg)": "3.500 kg",
                      "Nutzlast (kg)": "1.132,9 - 1.325,5 kg",
                      "Leerachslast, vorn (kg)": "1.286,2 - 1.377,4 kg",
                      "Leerachslast, hinten (kg)": "887,7 - 989,8 kg",
                      "Anhängelast gebremst (kg)": "2.050 kg",
                      "Anhängelast ungebremst (kg)": "750 kg",
                      "Zulässiges Gesamtgewicht des Zuges (kg)":
                        "5.065 - 5.700 kg",
                      Antrieb: "Frontantrieb",
                      Motor: "2.0 l EcoBlue 96 kW / 130 PS",
                      Getriebe: "6-Gang-Schaltgetriebe",
                    },
                    image: null,
                    link: {
                      href: "/mustang/",
                      target: null,
                      class: null,
                      title: null,
                      linkText: "Ford Mustang",
                      additionalAttributes: [],
                    },
                  },
                },
              },
            },
          },
          layout: "0",
        },
      },
    ],
  },
};
